import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { ApplicationType, DssUserRoles, FlightZoneApplicationStatus, FlightZoneRestrictionActions } from "@dtm-frontend/dss-shared-lib";
import { AuthState } from "@dtm-frontend/shared/auth";
import { Store } from "@ngxs/store";
import { mergeMap, of } from "rxjs";
import { FlightZoneApplicationActions } from "../state/flight-zone-application.actions";
import { FlightZoneApplicationState } from "../state/flight-zone-application.state";
import { FlightZoneListsActions } from "../state/flight-zone-lists.actions";
import { FlightZoneManagementActions } from "../state/flight-zone-management.actions";

export const FLIGHT_ZONE_MANAGEMENT_ID_ROUTE_PARAM_NAME = "id";
export const FLIGHT_ZONE_PREVIEW_ROUTE_SEGMENT = "preview";
export const FLIGHT_ZONE_APPLICATION_ROUTE_SEGMENT = "flight-zone";
export const FLIGHT_ZONE_MODIFICATION_ROUTE_SEGMENT = "modifications";

@Injectable()
export class ApplicationAssigneesResolver {
    constructor(private readonly store: Store) {}

    public resolve() {
        return this.store.dispatch(new FlightZoneManagementActions.GetAvailableApplicationAssignees());
    }
}

@Injectable()
export class FlightZoneManagementApplicationDataResolver {
    constructor(private readonly store: Store, private readonly router: Router) {}

    public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const flightZoneId: string = route.paramMap.get(FLIGHT_ZONE_MANAGEMENT_ID_ROUTE_PARAM_NAME) ?? "";
        const applicationType: ApplicationType = route.data.applicationType;
        const shouldSkipDataFetch = this.router.getCurrentNavigation()?.extras.state?.shouldSkipDataFetch;
        const isPreviewPathActivated = route.url.some(({ path }) => path === FLIGHT_ZONE_PREVIEW_ROUTE_SEGMENT);
        const hasSupervisorPermissions = this.store.selectSnapshot(AuthState.roles)?.includes(DssUserRoles.AnspSupervisor);

        if (shouldSkipDataFetch) {
            return this.router.navigate([state.url]);
        }

        return this.store.dispatch(new FlightZoneApplicationActions.GetApplicationData(flightZoneId, applicationType)).pipe(
            mergeMap(() => {
                const application = this.store.selectSnapshot(FlightZoneApplicationState.applicationData);
                const currentUserId = this.store.selectSnapshot(AuthState.userId);
                const baseRouteSegment =
                    applicationType === ApplicationType.RestrictionModificationApplication
                        ? FLIGHT_ZONE_MODIFICATION_ROUTE_SEGMENT
                        : FLIGHT_ZONE_APPLICATION_ROUTE_SEGMENT;

                if (!application?.status) {
                    return of(false);
                }

                const previewOnlyStatuses = [
                    FlightZoneApplicationStatus.Accepted,
                    FlightZoneApplicationStatus.Published,
                    FlightZoneApplicationStatus.Rejected,
                ];

                let isEditDisabledForApplication = previewOnlyStatuses.includes(application.status);
                if (!hasSupervisorPermissions) {
                    isEditDisabledForApplication =
                        [...previewOnlyStatuses, FlightZoneApplicationStatus.Reviewed].includes(application.status) ||
                        application.analysisStatus?.assignment?.reviewer.id !== currentUserId;
                }

                if (isEditDisabledForApplication && !isPreviewPathActivated) {
                    return this.router.navigate([baseRouteSegment, FLIGHT_ZONE_PREVIEW_ROUTE_SEGMENT, flightZoneId], {
                        state: { shouldSkipDataFetch: true },
                    });
                } else if (!isEditDisabledForApplication && isPreviewPathActivated) {
                    return this.router.navigate([baseRouteSegment, flightZoneId], {
                        state: { shouldSkipDataFetch: true },
                    });
                }

                return of(true);
            })
        );
    }
}

@Injectable()
export class FlightZoneManagementRestrictionDataResolver {
    constructor(private readonly store: Store) {}

    public resolve(route: ActivatedRouteSnapshot) {
        const restrictionId: string = route.paramMap.get(FLIGHT_ZONE_MANAGEMENT_ID_ROUTE_PARAM_NAME) ?? "";

        return this.store.dispatch(new FlightZoneRestrictionActions.GetRestrictionData(restrictionId));
    }
}

@Injectable()
export class ListFiltersCapabilitiesResolver {
    constructor(private readonly store: Store) {}

    public resolve() {
        return this.store.dispatch(new FlightZoneListsActions.GetListFiltersCapabilities());
    }
}

@Injectable()
export class AssignmentCapabilitiesResolver {
    constructor(private readonly store: Store) {}

    public resolve() {
        return this.store.dispatch(new FlightZoneManagementActions.GetAssignmentCapabilities());
    }
}

@Injectable()
export class NotamCapabilitiesResolver {
    constructor(private readonly store: Store) {}

    public resolve() {
        return this.store.dispatch(new FlightZoneManagementActions.GetNotamCapabilities());
    }
}

@Injectable()
export class NotamResolver {
    constructor(private readonly store: Store) {}

    public resolve(route: ActivatedRouteSnapshot) {
        const flightZoneId: string = route.paramMap.get(FLIGHT_ZONE_MANAGEMENT_ID_ROUTE_PARAM_NAME) ?? "";

        return this.store.dispatch(new FlightZoneManagementActions.GetNotamData(flightZoneId));
    }
}
